import { useMemo, useState } from "react";
import { taxIdLabel } from "@talentkontor/domain";
import { Link, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { ErrorState, Loading } from "../../components/States";
import { PillView } from "../../components/ui";
import { money } from "../../lib/format";
import { useContracts, useCreatorBase, type CPeriod } from "./data";
import {
  agencyShareOf, barterTaxNote, creatorOccurrences, extrasOfPeriod, creatorRules, flatTaxNote, isDirect, modelLabel, monthNeedsInvoice, monthPill, NO_REVENUE_MODELS, parseSuggestedItems, pill, platformLabel,
  amountsAreShare, rateOf, recipientOf, talentStateContext, viewTalentInvoice, type TalentInvoiceView,
} from "./model";
import { DemoNote, openDocument, useCreatorContext, useCreatorVisibility } from "./ui";
import { dateOf, monthOf, numOf, pctOf, useT } from "./sprache";
import { showsBudget } from "./sicht";
import { creatorErrorLine } from "./fehler";
import { ProvisionZahlen, feeOpen } from "./Provision";
import { DealBeitraege, gezeigteBeitraege } from "./Beitraege";
import { DealBriefing, useCreatorMarke } from "./marke";

/** Kooperation im Detail (12_app_creator/deal-detail.html, deal-oktober.html); ohne interne Agentur-Angaben. */

export function DealDetail() {
  const { dealId } = useParams();
  const [params] = useSearchParams();
  const { scope, today, lang } = useCreatorContext();
  const t = useT();
  const base = useCreatorBase(scope);
  const contracts = useContracts(scope);
  const b = base.data;
  const budgetVisible = showsBudget(useCreatorVisibility());

  const data = useMemo(() => {
    if (!b) return null;
    const deal = b.deals.find((d) => d.id === dealId);
    if (!deal) return { deal: null } as const;
    const periods = b.periods.filter((p) => p.deal_id === deal.id).sort((x, y) => x.month.localeCompare(y.month));
    const wanted = params.get("monat");
    const cur = today.slice(0, 7);
    const period: CPeriod | null = periods.find((p) => p.month.slice(0, 7) === wanted)
      ?? periods.find((p) => p.month.slice(0, 7) === cur)
      ?? periods.find((p) => p.month.slice(0, 7) > cur)
      ?? periods.at(-1)
      ?? null;
    const occ = creatorOccurrences({ today, talent: b.talent, deals: [deal], periods, lineItems: b.lineItems, invoices: b.invoices, bookings: [], rates: b.rates, agencyInvoices: b.agencyInvoices });
    const rules = creatorRules(b.defaults, b.org);
    const ctx = talentStateContext(today, rules, b.org.currency);
    const views = b.invoices.filter((i) => i.deal_id === deal.id).map((r) => viewTalentInvoice(r, ctx, b.org.timezone, lang, rules)).filter((v): v is TalentInvoiceView => !!v);
    // Positionen ohne Monat gehören zum ersten Monat der Kooperation (extrasOfPeriod, wie bei der Agentur)
    const extras = period ? extrasOfPeriod(b.lineItems, b.periods, period) : b.lineItems.filter((e) => e.deal_id === deal.id && !e.period_id);
    const viewMap = new Map(b.invoices.map((r) => viewTalentInvoice(r, ctx, b.org.timezone, lang, rules)).filter((v): v is TalentInvoiceView => !!v).map((v) => [v.row.id, v]));
    return { deal, periods, period, occ: period ? occ.find((o) => o.periodId === period.id) ?? null : null, views, extras, viewMap };
  }, [b, dealId, params, today, lang]);

  if (base.error) return <ErrorState error={base.error} retry={() => void base.refetch()} />;
  if (base.isPending || !b || !data) return <Loading />;
  if (!data.deal) {
    return (
      <main id="main" className="content tk-cr tk-cr-deal">
        <div className="panel stack tk-empty" role="alert">
          <h1 className="tk-state-title">{t("Diese Kooperation ist nicht (mehr) sichtbar.", "This collaboration is not (or no longer) visible.")}</h1>
          <p>{t("Sie gehört zu einer anderen Agentur, wurde archiviert oder ist für dich nicht freigegeben.", "It belongs to another agency, has been archived or is not shared with you.")}</p>
          <Link className="btn" to="/creator/deals">{t("Zu deinen Deals", "Go to your deals")}</Link>
        </div>
      </main>
    );
  }

  const { deal, periods, period, occ, views, extras, viewMap } = data;
  const monthCtx = { today, views: viewMap, selfBilling: !!b.talent.self_billing_agreed_on, ...(b.statusLabels ? { statusLabels: b.statusLabels } : {}) };
  const missing = !!period && monthNeedsInvoice(period, deal, b.invoices, today);
  const brand = b.brands.get(deal.brand_id)?.name ?? t("Marke", "Brand");
  const month = period?.month.slice(0, 7) ?? deal.start_month?.slice(0, 7) ?? null;
  const cur = deal.currency;
  const rate = rateOf(b, deal);
  const direct = isDirect(deal);
  const budget = deal.model === "commission_only" ? 0 : (period?.amount_cents ?? deal.budget_cents ?? 0);
  const extraTotal = extras.reduce((a, e) => a + e.amount_cents, 0);
  const monthViews = views.filter((v) => (period ? v.row.period_id === period.id : true));
  const extraViews = views.filter((v) => !v.row.period_id);
  const live = monthViews.filter((v) => !["withdrawn", "discarded", "cancelled"].includes(v.row.status));
  const fix = live.find((v) => v.state.state === "correction_needed");
  const draft = live.find((v) => v.state.state === "draft");
  const submittedAlready = live.some((v) => !["draft", "correction_needed"].includes(v.state.state));
  const noInvoice = NO_REVENUE_MODELS.has(deal.model) && !(deal.model === "barter" && (deal.budget_cents ?? 0) > 0);
  const dealContracts = (contracts.data?.contracts ?? []).filter((c) => c.deal_id === deal.id);
  const suggested = period ? parseSuggestedItems(period.suggested_items) : [];
  const deadline = period?.content_deadline ?? (periods.length <= 1 ? deal.content_deadline : null);
  const perDeliverable = gezeigteBeitraege(b, deal, periods, period).some((x) => x.status !== "cancelled");
  const fees = b.agencyInvoices.filter((i) => i.deal_id === deal.id && (!period || !i.period_id || i.period_id === period.id));
  const fmt = (d: string | null | undefined) => dateOf(d ?? null, lang, b.org.timezone);
  const contractPill: Record<string, ReturnType<typeof pill>> = {
    sent: pill(t("Zur Unterschrift", "To sign"), "info"),
    partially_signed: pill(t("Teilweise unterschrieben", "Partially signed"), "warning"),
    signed: pill(t("Unterschrieben", "Signed"), "success"),
    void: pill(t("Aufgehoben", "Void"), "neutral"),
  };

  let shareText: string;
  let shareNote: string;
  // Verständlichkeit E-06: bei direkter Abrechnung drei kurze Schritte, der Rechenweg darunter einklappbar
  let schritte: string[] | null = null;
  if (noInvoice) {
    shareText = t("Ohne Honorar", "No fee");
    shareNote = t(`${modelLabel(deal.model, lang)}: Für diese Kooperation gibt es kein Honorar und keine Rechnung.`, `${modelLabel(deal.model, lang)}: this collaboration has no fee and no invoice.`);
  } else if (occ?.shareCents != null && occ.actual) {
    shareText = t(`Dein Anteil: ${money(occ.shareCents, cur)}`, `Your share: ${money(occ.shareCents, cur)}`);
    shareNote = direct
      ? t(`Deine Rechnung an die Marke abzüglich der Provisionsrechnung deiner Agentur, netto.${budgetVisible ? ` Honorar netto ${money(budget + extraTotal, cur)}.` : ""}`, `Your invoice to the brand minus your agency's commission invoice, net.${budgetVisible ? ` Net fee ${money(budget + extraTotal, cur)}.` : ""}`)
      : t(`Laut deiner Rechnung für diesen Monat, netto.${budgetVisible ? ` Honorar netto ${money(budget + extraTotal, cur)}.` : ""}`, `According to your invoice for this month, net.${budgetVisible ? ` Net fee ${money(budget + extraTotal, cur)}.` : ""}`);
  } else if (occ?.shareCents != null && amountsAreShare(deal)) {
    // CREATOR-1: die Agentur zeigt nur den Anteil; die Beträge kommen schon als Anteil (0079)
    shareText = t(`Dein Anteil: ${money(occ.shareCents, cur)}`, `Your share: ${money(occ.shareCents, cur)}`);
    shareNote = t("Dein Betrag nach der Provision deiner Agentur, netto. Der Auszahlungsbetrag kann abhängig von deiner Rechnung Steuern enthalten.",
      "Your amount after your agency's commission, net. Depending on your invoice, the payout may include tax.");
  } else if (occ?.shareCents != null && rate) {
    shareText = t(`Dein Anteil: ${money(occ.shareCents, cur)}`, `Your share: ${money(occ.shareCents, cur)}`);
    // Staffel und Agenturvergütung der Marke wie bei der Agentur (agencyShareOf, GELD-3/GELD-4)
    const split = agencyShareOf(deal, budget, extras, rate, period?.month ?? null);
    const satz = `${numOf(Math.round(split.percent * 100) / 100, lang)} %${split.tiers ? t(" nach Staffel", " tiered") : ""}`;
    const abzug = split.discountCents > 0 && split.discount
      ? t(` minus Agenturvergütung der Marke ${money(split.discountCents, cur)} (${pctOf(split.discount.percent, lang)})`, ` minus the brand's agency discount ${money(split.discountCents, cur)} (${pctOf(split.discount.percent, lang)})`)
      : "";
    const anDieMarke = budget + extraTotal - split.discountCents;
    // Trägt die Agentur den Abzug der Marke, bleibt dein Anteil unberührt: Provision auf das volle Honorar, den Abzug zahlt die Agentur (GELD-25)
    const agenturTraegt = split.discountCents > 0 && split.discount?.bearer === "agency";
    const provisionVoll = split.agencyCents + (agenturTraegt ? split.discountCents : 0);
    const traegtText = agenturTraegt
      ? t(` Die Agenturvergütung der Marke (${money(split.discountCents, cur)}, ${numOf(split.discount!.percent, lang)} %) trägt deine Agentur; dein Anteil bleibt davon unberührt.`,
        ` Your agency bears the brand's agency discount (${money(split.discountCents, cur)}, ${numOf(split.discount!.percent, lang)} %); your share is not affected.`)
      : "";
    if (direct && budgetVisible) schritte = [
      t(`Du stellst der Marke in Rechnung: ${money(anDieMarke, cur)} netto.`, `You invoice the brand: ${money(anDieMarke, cur)} net.`),
      t(`Deine Agentur stellt dir ihre Provision in Rechnung: ${money(split.agencyCents, cur)}.`, `Your agency invoices you its commission: ${money(split.agencyCents, cur)}.`),
      t(`Dir bleiben: ${money(occ.shareCents, cur)}.`, `You keep: ${money(occ.shareCents, cur)}.`),
    ];
    // Befund 20: Markenbudget und Provision in Euro nur, wenn die Agentur sie ihren Creatorn zeigt
    shareNote = !budgetVisible
      ? direct
        ? t(`Du stellst der Marke das vereinbarte Honorar direkt in Rechnung; deine Agentur stellt dir ihre Provision (${satz}) getrennt in Rechnung.`, `You invoice the brand the agreed fee directly; your agency invoices you its commission (${satz}) separately.`)
        : t(`Dein Betrag nach der Provision deiner Agentur (${satz}), netto. Der Auszahlungsbetrag kann abhängig von deiner Rechnung Steuern enthalten.`, `Your amount after your agency's commission (${satz}), net. Depending on your invoice, the payout may include tax.`)
      : direct
        ? agenturTraegt
          ? t(`Du stellst der Marke das Honorar von ${money(anDieMarke, cur)} netto direkt in Rechnung (Honorar ${money(budget + extraTotal, cur)}${abzug}); deine Agentur stellt dir ihre Provision (${satz} auf das volle Honorar, ${money(provisionVoll, cur)}) abzüglich des Abzugs der Marke in Rechnung: ${money(split.agencyCents, cur)}.${traegtText}`,
            `You invoice the brand the fee of ${money(anDieMarke, cur)} net directly (fee ${money(budget + extraTotal, cur)}${abzug}); your agency invoices you its commission (${satz} on the full fee, ${money(provisionVoll, cur)}) less the brand's discount: ${money(split.agencyCents, cur)}.${traegtText}`)
          : t(`Du stellst der Marke das Honorar von ${money(anDieMarke, cur)} netto direkt in Rechnung${abzug ? ` (Honorar ${money(budget + extraTotal, cur)}${abzug})` : ""}; deine Agentur stellt dir ihre Provision (${satz}, ${money(split.agencyCents, cur)}) getrennt in Rechnung.`,
            `You invoice the brand the fee of ${money(anDieMarke, cur)} net directly${abzug ? ` (fee ${money(budget + extraTotal, cur)}${abzug})` : ""}; your agency invoices you its commission (${satz}, ${money(split.agencyCents, cur)}) separately.`)
        : agenturTraegt
          ? t(`Honorar netto ${money(budget + extraTotal, cur)} minus Agenturanteil ${money(provisionVoll, cur)} (${satz}).${traegtText} Der Auszahlungsbetrag kann abhängig von deiner Rechnung Steuern enthalten.`,
            `Net fee ${money(budget + extraTotal, cur)} minus agency share ${money(provisionVoll, cur)} (${satz}).${traegtText} Depending on your invoice, the payout may include tax.`)
          : t(`Honorar netto ${money(budget + extraTotal, cur)}${abzug} minus Agenturanteil ${money(split.agencyCents, cur)} (${satz}). Der Auszahlungsbetrag kann abhängig von deiner Rechnung Steuern enthalten.`,
            `Net fee ${money(budget + extraTotal, cur)}${abzug} minus agency share ${money(split.agencyCents, cur)} (${satz}). Depending on your invoice, the payout may include tax.`);
  } else if (deal.model === "commission_only") {
    shareText = modelLabel("commission_only", lang);
    shareNote = t("Für dieses Vergütungsmodell gibt es kein festes Honorar im Portal.", "This remuneration model has no fixed fee in the portal.");
  } else {
    shareText = budgetVisible ? t(`Budget der Marke: ${money(budget + extraTotal, cur)}`, `Brand budget: ${money(budget + extraTotal, cur)}`) : t("Dein Anteil folgt", "Your share to follow");
    shareNote = t("Für diese Kooperation ist kein Provisionssatz hinterlegt. Dein Anteil steht fest, sobald du deine Rechnung einreichst.", "No commission rate is on file for this collaboration. Your share is fixed once you submit your invoice.");
  }

  // Prüf-Chat Nachtest (03.11.2026), B: Monat storniert ohne Honorar: 0 € und keine Rechnung nötig (statt „Dein Anteil folgt“);
  // mit Ausfallhonorar heißt der Betrag so
  const ausfall = (period?.cancellation_fee_cents ?? 0) > 0;
  if (period && period.status === "cancelled" && !ausfall) {
    shareText = money(0, cur);
    shareNote = t("Dieser Monat ist storniert, ohne Honorar. Es gibt nichts abzurechnen; eine Rechnung ist nicht nötig.", "This month is cancelled without a fee. There is nothing to bill; no invoice is needed.");
  } else if (period && ausfall) {
    shareNote = t(`Für diesen Monat gilt ein Ausfallhonorar von ${money(period.cancellation_fee_cents ?? 0, cur)} netto. ${shareNote}`, `A cancellation fee of ${money(period.cancellation_fee_cents ?? 0, cur)} net applies to this month. ${shareNote}`);
  }

  const wizardLink = fix
    ? { to: `/creator/rechnungen/einreichen?rechnung=${fix.row.id}`, label: t("Rechnung korrigieren", "Correct invoice") }
    : draft
      ? { to: `/creator/rechnungen/einreichen?rechnung=${draft.row.id}`, label: t("Entwurf fertigstellen", "Finish draft") }
      : { to: `/creator/rechnungen/einreichen?kooperation=${deal.id}${month ? `&monat=${month}` : ""}`, label: t("Rechnung einreichen", "Submit invoice") };
  const recipient = recipientOf(b, deal, lang);

  return (
    <main id="main" className="content tk-cr tk-cr-deal">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{brand} · {deal.title}</h1>
          <p>{[modelLabel(deal.model, lang), deal.platforms?.map((p) => platformLabel(p, lang, b.platformNames)).join(", "), deal.deal_number != null ? `${t("Nr.", "No.")} ${deal.deal_number}` : null].filter(Boolean).join(" · ")}</p>
        </div>
        <Link className="btn" to="/creator/deals">{t("Zu deinen Deals", "Go to your deals")}</Link>
      </div>

      {periods.length > 1 && (
        <nav className="tabs" aria-label={t("Monate der Kooperation", "Months of the collaboration")}>
          {periods.map((p) => {
            const m = p.month.slice(0, 7);
            const current = p.id === period?.id;
            return (
              <Link key={p.id} className={current ? "current" : ""} aria-current={current ? "page" : undefined} to={`/creator/deals/${deal.id}?monat=${m}`}>
                {monthOf(m, lang)}
              </Link>
            );
          })}
        </nav>
      )}

      <section className="panel stack">
        <h3>{month ? monthOf(month, lang) : t("Ohne Monat", "No month")}</h3>
        <PillView pill={period ? monthPill(period, deal, b.invoices, lang, monthCtx) : pill(t("Offen", "Open"), "neutral")} />
        <h2 className="num">{shareText}</h2>
        {schritte ? (
          <>
            <ol>{schritte.map((x) => <li key={x}>{x}</li>)}</ol>
            <details><summary>{t("So rechnen wir", "How we calculate")}</summary><p>{shareNote}</p></details>
          </>
        ) : <p>{shareNote}</p>}
        {deal.status === "blocked" && (
          <p className="notice">{t("Deine Agentur hat diese Kooperation angehalten, etwa weil ein Vertrag, eine Freigabe oder eine Zahlung der Marke fehlt. Den Grund nennt sie dir im Chat.",
            "Your agency has put this collaboration on hold, for example because a contract, an approval or a payment from the brand is missing. It tells you the reason in the chat.")}</p>
        )}
        {missing && !b.talent.self_billing_agreed_on && !period?.invoice_requested_at && (
          <p className="notice">{ausfall
            ? t("Für diesen Monat steht ein Ausfallhonorar fest, deine Rechnung über das Ausfallhonorar fehlt noch. Deine Agentur hat sie noch nicht angefordert; ohne Rechnung wird nicht ausgezahlt.",
              "A cancellation fee is fixed for this month; your invoice for the cancellation fee is still missing. Your agency has not requested it yet; without an invoice there is no payout.")
            : t("Dieser Monat ist geliefert, aber deine Rechnung fehlt noch. Deine Agentur hat sie noch nicht angefordert; ohne Rechnung wird nicht ausgezahlt.",
              "This month is delivered but your invoice is still missing. Your agency has not requested it yet; without an invoice there is no payout.")}</p>
        )}
        {missing && !!b.talent.self_billing_agreed_on && (
          <p className="notice">{t("Dieser Monat ist geliefert. Deine Agentur rechnet per Gutschrift für dich ab; du musst keine Rechnung schreiben.",
            "This month is delivered. Your agency bills for you by self-billed invoice; you do not need to write an invoice.")}</p>
        )}
        {period && (period.manual_status ?? deal.status) === "content_approval" && (
          <p className="notice">{t("Dein Content ist in der Freigabe. Jetzt ist deine Agentur bzw. die Marke am Zug; du bekommst Bescheid, wenn es Änderungswünsche gibt.",
            "Your content is in approval. Now your agency or the brand is up next; you will be told if there are change requests.")}</p>
        )}
        {deal.model === "barter" && <p className="notice">{barterTaxNote(lang)}</p>}
        {extras.length > 0 && (
          <ul>
            {extras.map((e) => (
              <li key={e.id}>{e.description}: <span className="num">{money(e.amount_cents, cur)}</span>{e.commissionable ? "" : t(" · provisionsfrei", " · commission-free")}</li>
            ))}
          </ul>
        )}
      </section>

      <section className="panel stack">
        <h3>{t("Was du abgibst", "What you deliver")}</h3>
        {/* Paket 2: Eckdaten als Kacheln wie im Original (Umfang, Plattformen, Nutzungsrechte, Content-Abgabe), der Rest als Wertzeilen */}
        <dl className="tk-cr-kacheln">
          <div><dt>{t("Umfang", "Scope")}</dt><dd>{deal.deliverables?.trim() || t("Umfang folgt von deiner Agentur.", "Scope to follow from your agency.")}</dd></div>
          {deal.platforms && deal.platforms.length > 0 && <div><dt>{t("Plattformen", "Platforms")}</dt><dd>{deal.platforms.map((p) => platformLabel(p, lang, b.platformNames)).join(", ")}</dd></div>}
          {deal.usage_rights && <div><dt>{t("Nutzungsrechte", "Usage rights")}</dt><dd>{deal.usage_rights.replace(/^Nutzungsrechte\s*·\s*/, "")}{deal.usage_rights_until ? t(` · bis ${fmt(deal.usage_rights_until)}`, ` · until ${fmt(deal.usage_rights_until)}`) : ""}</dd></div>}
          {/* CREATOR-R2-7: mit Beiträgen gilt die Frist je Beitrag (Übersicht, Kalender, Erinnerung); die Monatsfrist stünde daneben im Widerspruch */}
          <div><dt>{t("Content-Abgabe", "Content delivery")}</dt><dd>{perDeliverable
            ? <>{t("je Beitrag, siehe", "per post, see")} <a href="#beitraege">{t("„Deine Beiträge“", "“Your content”")}</a></>
            : deadline ? fmt(deadline) : t("noch offen", "not yet set")}</dd></div>
        </dl>
        {(deal.products_flat_taxed || deal.link) && (
          <dl className="tk-cr-werte">
            {deal.products_flat_taxed && <div><dt>{t("Produkte pauschal versteuert", "Products taxed at a flat rate")}</dt><dd>{flatTaxNote(lang, b.org.country ?? null)}</dd></div>}
            {deal.link && <div><dt>Link</dt><dd><a href={deal.link} target="_blank" rel="noopener noreferrer">{deal.link}</a></dd></div>}
          </dl>
        )}
        {deal.talent_note && <div className="revision-note">{deal.talent_note}</div>}
        {/* Eine eigene Content-Ablage gibt es nicht; kein gesperrtes Dateifeld, nur der Weg (Befund 24) */}
        {/* Mit Beiträgen läuft die Abgabe über „Deine Beiträge“; der Chat-Weg gilt nur für Kooperationen ohne Beiträge */}
        {gezeigteBeitraege(b, deal, periods, period).length > 0
          ? <p>{t("Entwürfe reichst du unten unter „Deine Beiträge“ ein; dort meldest du auch, wenn ein Beitrag live ist.", "Submit your drafts below under “Your content”; that is also where you report a post as live.")} <a href="#beitraege">{t("Zu deinen Beiträgen", "Go to your content")}</a></p>
          // Prüf-Chat Nachtest (03.11.2026), Befund 2: bei Storno kein Upload anbieten (media_upload_start lehnt ab), sondern sagen warum
          : deal.status === "cancelled" || period?.status === "cancelled"
            ? <p className="notice">{deal.status === "cancelled" ? t("Diese Kooperation ist storniert. Content gibst du hier nicht mehr ab; Fragen klärst du im Chat mit deiner Agentur.", "This collaboration is cancelled. You no longer submit content here; ask your agency in the chat if anything is unclear.")
              : t("Dieser Monat ist storniert. Content gibst du für ihn nicht mehr ab.", "This month is cancelled. You no longer submit content for it.")}</p>
            : <ContentSelbstStarten dealId={deal.id} periodId={period?.id ?? null} gesperrt={deal.status === "blocked"} />}
      </section>

      {/* 0073: Briefing der Kooperation (Rückfall: Briefing der Kampagne) */}
      <DealBriefingLaden dealId={deal.id} />

      <DealBeitraege b={b} deal={deal} periods={periods} period={period} today={today} />

      {(period?.invoice_requested_at || monthViews.length > 0 || extraViews.length > 0) && (
        <section className="panel stack">
          <h3>{t(`Rechnung für ${month ? monthOf(month, lang) : "diesen Monat"}`, `Invoice for ${month ? monthOf(month, lang) : "this month"}`)}</h3>
          {period?.invoice_requested_at && (
            <div className="notice stack tk-gap-sm">
              <p><strong>{t(`Deine Agentur hat die Rechnung am ${fmt(period.invoice_requested_at)} angefordert.`, `Your agency requested the invoice on ${fmt(period.invoice_requested_at)}.`)}</strong></p>
              {period.request_note && <p>{period.request_note}</p>}
              {suggested.length > 0 && (
                <ul>
                  {suggested.map((s) => (
                    <li key={s.key}>{s.quantity !== 1 ? `${numOf(s.quantity, lang)} × ` : ""}{s.description}{s.unitCents ? `: ${money(s.unitCents, cur)}` : ""}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {[...monthViews, ...extraViews.filter((v) => !monthViews.includes(v))].map((v) => (
            <Link key={v.row.id} className="task" to={`/creator/rechnungen?rechnung=${v.row.id}`}>
              <div>
                <strong>{v.row.number ?? t("Entwurf ohne Nummer", "Draft without number")}</strong>
                <p>{fmt(v.row.issue_date)} · <span className="num">{money(v.row.gross_cents, v.row.currency)}</span> {t("brutto", "gross")}{!v.row.period_id ? t(" · ohne Monat", " · no month") : ""}</p>
              </div>
              <PillView pill={v.pill} />
            </Link>
          ))}
        </section>
      )}

      {direct && <ProvisionZahlen invoices={fees} orgId={b.org.org_id} orgName={b.org.name} today={today} title={t("Provisionsrechnung bezahlen", "Pay commission invoice")} />}
      {direct && fees.some((i) => !feeOpen(i)) && (
        <section className="panel stack">
          <h3>{t("Provisionsrechnung deiner Agentur", "Commission invoice from your agency")}</h3>
          {fees.filter((i) => !feeOpen(i)).map((i) => (
            <div key={i.id} className="row between">
              <span>{i.number ?? "–"} · <span className="num">{money(i.gross_cents, i.currency)}</span> · {i.status === "paid" ? t("bezahlt", "paid") : t(`fällig ${fmt(i.effective_due_date ?? i.document_due_date)}`, `due ${fmt(i.effective_due_date ?? i.document_due_date)}`)}</span>
              {i.pdf_path && <button onClick={() => void openDocument(i.pdf_path!, `${i.number ?? "rechnung"}.pdf`)}>{t("PDF öffnen", "Open PDF")}</button>}
            </div>
          ))}
        </section>
      )}

      <section className="panel stack">
        <h3>{t("Dokumente", "Documents")}</h3>
        {dealContracts.map((c) => (
          <div key={c.id} className="row between">
            <span>{c.title} <PillView pill={contractPill[c.status] ?? pill(c.status, "neutral")} /></span>
            <Link className="btn " to={`/creator/vertraege/${c.id}`}>{t("Vertrag ansehen", "View contract")}</Link>
          </div>
        ))}
        {dealContracts.length === 0 && <p>{contracts.isPending ? t("Verträge werden geladen …", "Loading contracts …") : t("Für diese Kooperation liegt noch kein Vertrag vor.", "There is no contract for this collaboration yet.")}</p>}
        {!noInvoice && !["blocked", "cancelled"].includes(deal.status) && (
          submittedAlready && !fix && !draft ? (
            <>
              <p>{t("Für diesen Monat liegt deine Rechnung vor. Den Stand siehst du unter „Rechnungen“.", "Your invoice for this month is available. You can see its status under “Invoices”.")}</p>
              <Link className="btn" to={`/creator/rechnungen/einreichen?kooperation=${deal.id}&zweck=extra`}>{t("Nachberechnung einreichen", "Submit additional billing")}</Link>
            </>
          ) : (
            <>
              <Link className="btn primary" to={wizardLink.to}>{wizardLink.label}</Link>
              <small>{`${direct ? t("Deine Rechnung geht direkt an die Marke", "Your invoice goes directly to the brand") : t("Deine Rechnung geht an deine Agentur", "Your invoice goes to your agency")}: ${[recipient.name, ...recipient.lines].filter(Boolean).join(", ")}${recipient.vatId ? ` · ${taxIdLabel(recipient.country, lang)} ${recipient.vatId}` : ""}.${direct ? t(" Deine Agentur stellt dir ihre Provision getrennt in Rechnung.", " Your agency invoices you its commission separately.") : ""}`}</small>
            </>
          )
        )}
      </section>

      <section className="panel stack">
        <h3>{t("Fragen zum Deal?", "Questions about the deal?")}</h3>
        <Link className="btn " to={`/creator/nachrichten?objekt=deal:${dealId}`}>{t("Deiner Agentur schreiben", "Write to your agency")}</Link>
      </section>
      <DemoNote />
    </main>
  );
}

function DealBriefingLaden({ dealId }: { dealId: string }) {
  const q = useCreatorMarke(dealId, []);
  return q.data ? <DealBriefing briefing={q.data.dealBriefing} /> : null;
}

/**
 * Praxistest 13.10.2026 (Inhaber: „baue, sodass es geht“): Ohne angelegte Beiträge stand hier nur „schick deinen Content im
 * Chat“; der Chat nimmt aber keine Videos. Der Creator startet die Abgabe jetzt selbst (0209): Die Datenbank legt einen
 * allgemeinen Beitrag „Content“ an, danach erscheint „Deine Beiträge“ mit dem Upload für Video, Bild und PDF.
 */
function ContentSelbstStarten({ dealId, periodId, gesperrt }: { dealId: string; periodId: string | null; gesperrt: boolean }) {
  const t = useT();
  const { lang } = useCreatorContext();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const starten = async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.rpc("deliverable_self_create", { p_deal: dealId, p_period: periodId });
      await qc.invalidateQueries({ queryKey: ["creator"] });
      window.setTimeout(() => document.getElementById("beitraege")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    } catch (e) {
      setFehler(creatorErrorLine(e, lang));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <p>{t("Videos, Bilder und PDF lädst du direkt hier hoch; deine Agentur prüft sie und gibt sie frei. Kurze Fragen und einzelne Bilder gehen auch im Chat.",
        "Upload videos, images and PDFs right here; your agency reviews and approves them. Quick questions and single images also work via chat.")}</p>
      <div className="row">
        <button type="button" className="primary" disabled={busy || gesperrt} aria-busy={busy} onClick={() => void starten()}>{busy ? t("Einen Moment …", "One moment …") : t("Video oder Datei hochladen", "Upload video or file")}</button>
        <Link className="btn" to="/creator/nachrichten">{t("Zum Chat", "Go to chat")}</Link>
      </div>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </div>
  );
}
